import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { DocumentFindLeadsMetadata } from "../../../lib/hooks/api/leads/useCreateLeadOneClick";
import { useLeadsSavedToEntities } from "../../../lib/hooks/api/leads/useLeadsSavedToEntities";
import { EventData } from "../../../lib/tracking";
import { SharedSaveProps } from "../../entities/utils";
import { CreateLeadFormOutput } from "../create_lead/utils";
import SaveLeadDrawer from "./SaveLeadDrawer";

type DrawerData = SharedSaveProps<CreateLeadFormOutput> & {
  buyerMatchOnly?: boolean;
  findLeadsMetadata?: DocumentFindLeadsMetadata;
  onLeadSaved?: (leadId: string) => void;
};

type SaveLeadDrawerContextValue = {
  openLeadDrawer: (data: NonNullable<DrawerData>) => void;
  closeLeadDrawer: () => void;
};

const SaveLeadDrawerContext = createContext<SaveLeadDrawerContextValue | null>(null);

export function useSaveLeadDrawer() {
  const ctx = useContext(SaveLeadDrawerContext);
  if (!ctx) throw new Error("useSaveLeadDrawer must be used within SaveLeadDrawerProvider");
  return ctx;
}

/**
 * Provides a single, shared SaveLeadDrawer instance for all consumers.
 *
 * This avoids rendering one drawer per table row by centralising drawer state
 * and dynamically supplying the active lead data when a row requests it.
 * Improves performance and ensures only one drawer can be open at a time.
 */
export function SaveLeadDrawerProvider({
  trackingData,
  children,
}: {
  trackingData: EventData;
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<DrawerData | null>(null);

  // Look up by the stored lead entity_id. Most types use `entityId` directly; Cognism passes
  // its redeemed-contact row id as `savedEntityId` (its `entityId` is a redeemId, not a stored
  // uuid). A Cognism row with no `savedEntityId` (not yet redeemed) can't be on a lead → [].
  const savedToLookupIds = (() => {
    if (!data) return [];
    if (data.savedEntityId) return [data.savedEntityId];
    return data.entityType !== "contact_cognism" ? [data.entityId] : [];
  })();
  const { data: savedLeads } = useLeadsSavedToEntities(savedToLookupIds);
  const savedLeadIds = savedLeads?.map((l) => l.id) ?? [];

  const openLeadDrawer = useCallback((next: DrawerData) => {
    setData(next);
    setIsOpen(true);
  }, []);

  const closeLeadDrawer = useCallback(() => {
    setIsOpen(false);
    setData(null);
  }, []);

  const value = useMemo(
    () => ({ openLeadDrawer, closeLeadDrawer }),
    [openLeadDrawer, closeLeadDrawer],
  );

  return (
    <SaveLeadDrawerContext.Provider value={value}>
      {children}

      {/* Single shared drawer instance */}
      {data && (
        <SaveLeadDrawer
          {...data}
          savedLeadIds={savedLeadIds}
          trackingData={trackingData}
          isOpen={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open);
            if (!open) setData(null);
          }}
          onLeadSaved={data.onLeadSaved}
        />
      )}
    </SaveLeadDrawerContext.Provider>
  );
}
